import { Injectable } from '@angular/core';
import { LocalStorageService } from './local.storage.service';

@Injectable({
  providedIn: 'root'
})
export class ReportFilterStorageService {
  private readonly STORAGE_KEY = 'report-filters';


  constructor(
    private _storageService: LocalStorageService
  ) { }

  /**
   * Salva os filtros de um relatório no localStorage.
   * Substitui qualquer filtro salvo anteriormente.
   * 
   * @param reportType Tipo do relatório
   * @param filters Objeto com os filtros do relatório
   */
  async saveFilters<T>(reportType: ReportsTypeEnum, filters: T): Promise<void> {
    if (!filters || typeof filters !== 'object') {
      console.error('[ReportFilterStorage] Filtros inválidos:', filters);
      return;
    }

    try {
      const data: ReportFilterStorageModel<T> = {
        reportType,
        filters
      };
      this._storageService.saveDataJSON(this.STORAGE_KEY, data);
    } catch (error) {
      console.error('[ReportFilterStorage] Erro ao salvar filtros:', error);
    }
  }

  /**
   * Recupera os filtros salvos do localStorage.
   * 
   * @returns Dados dos filtros salvos ou null se não houver
   */
  getFilters<T>(reportType: ReportsTypeEnum): ReportFilterStorageModel<T> | null {
    try {
      const data = this._storageService.getDataJSON(this.STORAGE_KEY);
      
      if (!data) {
        return null;
      }

      if (data.reportType !== reportType){
        return null;
      }

      return data as ReportFilterStorageModel<T>;
    } catch (error) {
      console.error('[ReportFilterStorage] Erro ao recuperar filtros:', error);
      return null;
    }
  }

  /**
   * Remove todos os filtros salvos do localStorage.
   */
  clearFilters(): void {
    try {
      this._storageService.remove(this.STORAGE_KEY);
    } catch (error) {
      console.error('[ReportFilterStorage] Erro ao limpar filtros:', error);
    }
  }

  /**
   * Limpa os filtros salvos se forem de um relatório diferente do atual.
   * Isso evita que filtros de um relatório sejam aplicados em outro.
   * 
   * @param currentReport Tipo do relatório atual
   */
  clearFiltersIfDifferentReport(currentReport: ReportsTypeEnum): void {
    try {
      const savedData = this.getFilters(currentReport);
      
      if (savedData && savedData.reportType !== currentReport) {
        this.clearFilters();
      }
    } catch (error) {
      console.error('[ReportFilterStorage] Erro ao verificar e limpar filtros:', error);
    }
  }
}

export enum ReportsTypeEnum {
  SALES_ANALYSIS = 1,    // Análise de vendas
  SALES_WORKSHOP = 2,    // Análise de vendas da oficina
  COMMISSIONS = 3,       // Análise de comissões por consultor e mecânico
  USER_SALES = 4,        // Análise de vendas por consultor e mecânico
  DRE = 5,               // DRE
  BUDGET_PROSPECTION = 6 // Análise de prospecção de orçamentos
}

export interface ReportFilterStorageModel<T> {
  reportType: ReportsTypeEnum;
  filters: T;
}